/**
 * Starts a download of multiple assets in a zip file by clicking an element (typically a button).
 *
 * This component uses the "render prop" approach. The button used to open the popup should be
 * rendered within a function passed in as props.children (aka render prop). The actual element that
 * the user clicks needs to use buttonProps.
 *
 * <MultiAssetDownload items={...}>
 *   {({ buttonProps }) => {
 *     return <Button {...buttonProps} text="Download" />
 *   }}
 * </MultiAssetDownload>;
 *
 * Also provided to the render function is numItems, which gives how many items are set to be
 * downloaded (and excludes non-downloadable items, such as colors)
 *
 * <MultiAssetDownload items={...}>
 *   {({ buttonProps, numItems }) => {
 *     return (
 *        <Button
 *          {...buttonProps}
 *          text={`Download ${numItems} Files`}
 *        />
 *      );
 *   }}
 * </MultiAssetDownload>;
 */

import React, { useCallback, useMemo } from "react";
import { AssetType, ErrorCode, SpacePermission, ItemType } from "@thenounproject/lingo-core";

import { Inspectable } from "@constants/Inspector";
import useDownloadMultipleAssets from "@actions/useDownloadMultipleAssets";
import useNotifications from "@actions/useNotifications";
import useShowModal, { ModalTypes } from "@redux/actions/useModals";
import { useSelectSpace } from "@selectors/entities/spaces";

type RenderProps = {
  buttonProps: {
    onClick: () => void;
  };
  numItems: number;
};

type Props = {
  inspectables: Inspectable[];
  children: (renderProps: RenderProps) => React.ReactNode;
};

export default function MultiAssetDownload({ inspectables, children }: Props) {
  const [downloadMultipleAssets] = useDownloadMultipleAssets();
  const { showNotification } = useNotifications();
  const { showModal, dismissModal } = useShowModal();
  const space = useSelectSpace();

  const filteredAssetInspectables = useMemo(
    () =>
      inspectables.filter(i => {
        const nonDownloadableItemTypes = [
          ItemType.supportingImage,
          ItemType.guide,
          ItemType.gallery,
        ];
        return Boolean(
          i?.asset?.permalink &&
            i?.asset?.type !== AssetType.URL &&
            !nonDownloadableItemTypes.includes(i?.item?.type)
        );
      }),
    [inspectables]
  );
  const downloadAssets = useCallback(
    async (e?: React.MouseEvent) => {
      if (e) {
        const event = e;
        event.preventDefault();
        event.stopPropagation();
      }
      showNotification({
        message: `Preparing to download ${filteredAssetInspectables.length} assets.`,
      });

      const useLibrary =
        space?.access?.permissions?.includes(SpacePermission.viewAssetLibrary) === true;

      const action = await downloadMultipleAssets({
        inspectables: filteredAssetInspectables,
        isAdmin: useLibrary,
      });

      if (action.error) {
        const { message } = action.error || {};
        if (action.error.code === ErrorCode.downloadRequiresIdentity) {
          return showModal(ModalTypes.INFO_PROMPT, {
            callback: () => downloadAssets(),
          });
        }
        if (action.error.code === ErrorCode.downloadRequestPending) {
          return showModal(ModalTypes.CONFIRMATION, {
            title: "Request Pending",
            message,
            buttonText: "Continue",
            buttonProcessingText: "Continue",
            onConfirm: dismissModal,
          });
        }
        if (action.error.code === ErrorCode.downloadRequiresVerifiedIdentity) {
          return showModal(ModalTypes.CONFIRMATION, {
            title: "Request Approved",
            message,
            buttonText: "Continue",
            buttonProcessingText: "Continue",
            onConfirm: dismissModal,
          });
        }
        if (action.error.code === ErrorCode.downloadRequestDenied) {
          return showModal(ModalTypes.CONFIRMATION, {
            title: "Request Denied",
            message,
            buttonText: "Continue",
            buttonProcessingText: "Continue",
            onConfirm: dismissModal,
          });
        }
        showNotification({
          level: "error",
          message:
            message ||
            "Unfortunately, there was a problem starting your download.  Please try again later.",
        });
      }
    },
    [
      showNotification,
      filteredAssetInspectables,
      space?.access?.permissions,
      downloadMultipleAssets,
      showModal,
      dismissModal,
    ]
  );

  return (
    <>
      {children({
        buttonProps: { onClick: downloadAssets },
        numItems: filteredAssetInspectables.length,
      })}
    </>
  );
}
